import * as d3 from 'd3'
import maps from './maps'

// ----------------------------------------------------------------
//
//  Custom Shade Generator
//
// ----------------------------------------------------------------

// Creates a custom palette based on Vuetify's built in Material Design colors
// This palette is smoother and generally less bright than the stock palettes
// It is used to create both light and dark themes in src/plugins/vuetify.js
// It can also be used here to generate various palettes
export const custom_shades = colors => {
  return Object.keys(colors).reduce((acc, c) => {
    const original = colors[c]
    let color = d3.hsl(original)

    if(!isNaN(color.s) && !isNaN(color.l)) {
      const inc = (1.0 - color.l) / 10.4
      const result = {base: color.hex()}
      for(let i = 0; i < 10; i++) {
        color.l += inc
        result[`lighten${i+1}`] = color.hex()
      }

      color = d3.hsl(original)
      const dec = color.l / 10.4
      for(let i = 0; i < 10; i++) {
        color.l -= dec
        result[`darken${i+1}`] = color.hex()
      }

      acc[c] = result
    }
    return acc
  }, {})
}

// ----------------------------------------------------------------
//
//  Candela Palette
//
// ----------------------------------------------------------------

export const black = '#000000'
export const blue = '#1574C2'
export const brown = '#9E6230'
export const green = '#1FBE67'
export const grey = '#bdbdbd'
export const orange = '#DF7238'
export const pea = '#dfeB84'
export const pink = '#da7FE5'
export const purple = '#945EE5'
export const red = '#ef5350'
export const transparent = '#00000000'
export const turquoise = '#1FB0BD'
export const white = '#ffffff'
export const yellow = '#E2BC4C'

export const blue_dark = '#0D4472'
export const brown_dark = '#5C3A1E'
export const green_dark = '#106336'
export const orange_dark = '#733312'
export const pea_dark = '#5D6510'
export const pink_dark = '#6D2D73'
export const purple_dark = '#54308B'
export const red_dark = '#9D2A28'
export const turquoise_dark = '#105E63'
export const yellow_dark = '#826515'

export const blue_light = '#71B6EF'
export const brown_light = '#D9A982'
export const green_light = '#80EAB0'
export const orange_light = '#EBA480'
export const pea_light = '#F5F8D6'
export const pink_light = '#F1C3F5'
export const purple_light = '#C6A8F4'
export const red_light = '#F48A88'
export const turquoise_light = '#90E7ED'
export const yellow_light = '#EDD490'

export const vuetify_theme_dark = custom_shades({
  info: blue,
  success: green,
  secondary: orange,
  primary: yellow,
  grey,
  error: red,
  warning: orange,
  blue,
  orange,
  red,
  turquoise,
  yellow,
  green,
  purple,
  pink,
  brown,
  pea
})

export const vuetify_theme_light = custom_shades({
  info: blue,
  success: green,
  secondary: yellow,
  primary: orange,
  grey,
  error: red,
  warning: orange,
  blue,
  orange,
  red,
  turquoise,
  yellow,
  green,
  purple,
  pink,
  brown,
  pea
})

// ----------------------------------------------------------------
//
//  Discrete
//
// ----------------------------------------------------------------

/**
* Creates a cycling discrete color palette
*/
export const create_color_palette = options => {
  let i = 0
  const colors = options.colors || candela_cat_10.colors
  const length = colors.length
  const result = function(idx) {
    if(!isNaN(idx)) return colors[idx % length]
    return colors[i++ % length]
  }

  Object.defineProperty(result, 'colors', {
    get: function() { return [...colors] }
  })

  Object.defineProperty(result, 'description', {
    get: function() { return options.description || null }
  })

  Object.defineProperty(result, 'display_name', {
    get: function() { return options.display_name || 'Unnamed Color Palette' }
  })

  Object.defineProperty(result, 'reference', {
    get: function() { return options.reference || null }
  })

  return result
}

export const candela_cat_10 = create_color_palette({
  colors: [
    blue, orange, red, turquoise, yellow, green, purple, pink, brown, pea
  ],
  description: 'Candela\'s default discrete color palette',
  display_name: 'Candela Categorical 10',
  reference: ''
})

export const candela_cat_20 = create_color_palette({
  colors: [
    blue_dark, blue,
    orange_dark, orange,
    red_dark, red,
    turquoise_dark, turquoise,
    yellow_dark, yellow,
    green_dark, green,
    purple_dark, purple,
    pink_dark, pink,
    brown_dark, brown,
    pea_dark, pea
  ],
  description: '20 tone variant of the default 10 color discrete palette',
  display_name: 'Candela Categorical 20',
  reference: ''
})

export const candela_cat_30 = create_color_palette({
  colors: [
    blue_dark, blue, blue_light,
    orange_dark, orange, orange_light,
    red_dark, red, red_light,
    turquoise_dark, turquoise, turquoise_light,
    yellow_dark, yellow, yellow_light,
    green_dark, green, green_light,
    purple_dark, purple, purple_light,
    pink_dark, pink, pink_light,
    brown_dark, brown, brown_light,
    pea_dark, pea, pea_light
  ],

  description: '30 tone variant of the default 10 color discrete palette',
  display_name: 'Candela Categorical 30',
  reference: ''
})

export const earth_cat_9 = create_color_palette({
  colors: [
    '#f67838', '#fdb03f', '#7e756c', '#bfbb56',
    '#a1cea8', '#d7ce9b', '#9669DA', '#4f6981',
    '#b9aa96'
  ],
  description: 'Muted Earth tones',
  display_name: 'Earth 9'
})

export const tetons = create_color_palette({
  colors: ['#98abc5', '#8a89a6', '#7b6888', '#6b486b', '#a05d56', '#d0743c', '#ff8c00'],
  display_name: 'Tetons'
})

export const discrete = [
  candela_cat_10,
  // candela_cat_20,
  // candela_cat_30,
  earth_cat_9
]

// ----------------------------------------------------------------
//
//  Spectral
//
// ----------------------------------------------------------------

/**
 * Creates a continuous color gradient function
 * Returns a function that expects a value on the interval [0, 1]
 */
const create_color_spectrum = options => {
  const colors = options.colors || candela_cat_10.colors
  const length = colors.length
  const domain = Array(length).fill(0).map((x, i) => (i / (length-1)))
  const result =
    options.interpolator ||
      d3.scaleLinear()
        .domain(domain)
        .range(options.colors || candela_cat_10.colors)
        .interpolate(d3.interpolateRgb)

  Object.defineProperty(result, 'description', {
    get: function() { return options.description || null }
  })

  Object.defineProperty(result, 'display_name', {
    get: function() { return options.display_name || 'Unnamed Color Palette' }
  })

  Object.defineProperty(result, 'reference', {
    get: function() { return options.reference || null }
  })

  return result
}

// -- Cyclical

export const rainbow = create_color_spectrum({
  interpolator: d3.interpolateRainbow,
  display_name: 'Rainbow',
  description: 'A cyclical, less angry rainbow',
  reference: 'https://github.com/d3/d3-scale-chromatic/blob/master/README.md#interpolateRainbow'
})

export const sinebow = create_color_spectrum({
  interpolator: d3.interpolateSinebow,
  display_name: 'Sinebow',
  description: 'A cyclical rainbow based on “sinebow” color scheme by Jim Bumgardner and Charlie Loyd',
  reference: 'https://krazydad.com/tutorials/makecolors.php'
})

export const cyclical = [
  rainbow,
  sinebow
]

// -- Diverging

export const curl = create_color_spectrum({
  colors: maps.curl,
  description: 'A diverging spectrum from darker teal to just off-white through shades of magenta',
  display_name: 'Curl',
  reference: 'https://matplotlib.org/cmocean/'
})

export const roygbiv = create_color_spectrum({
  description: 'A diverging spectrum of red, orange, yellow, green, blue, indigo, violet',
  display_name: 'Roygbiv',
  interpolator: d3.interpolateSpectral,
  reference: 'https://github.com/d3/d3-scale-chromatic/blob/master/README.md#schemeSpectral'
})

export const ryg = create_color_spectrum({
  description: 'A diverging spectrum from red to yellow to green',
  display_name: 'Red to Yellow to Green',
  interpolator: d3.interpolateRdYlGn,
  reference: 'https://github.com/d3/d3-scale-chromatic/blob/master/README.md#interpolateRdYlGn'
})

export const surf = create_color_spectrum({
  description: 'A diverging spectrum from rich brown to deep blue green',
  display_name: 'Surf',
  interpolator: d3.interpolateBrBG,
  reference: 'https://github.com/d3/d3-scale-chromatic/blob/master/README.md#interpolateBrBG'
})

export const diverging = [
  curl,
  roygbiv,
  ryg,
  surf
]

// -- Sequential Single Hue

export const greyscale = create_color_spectrum({
  description: 'A continuous sequential spectrum from white to black',
  display_name: 'Grayscale',
  interpolator: d3.interpolateGreys,
  reference: 'https://github.com/d3/d3-scale-chromatic/blob/master/README.md#interpolateGreys'
})

export const blues = create_color_spectrum({
  description: 'A continuous sequential spectrum from light to dark blue',
  display_name: 'The Blues',
  colors: ['#c2e0f8', blue, '#093152'],
  reference: ''
})

export const greens = create_color_spectrum({
  description: 'A continuous sequential spectrum from light to dark green',
  display_name: 'The Greens',
  colors: ['#defaea', green, '#0a3e22'],
  reference: ''
})

export const oranges = create_color_spectrum({
  description: 'A continuous sequential spectrum from light to dark orange',
  display_name: 'Oranges',
  colors: ['#fbece4', orange, '#4f240d'],
  reference: ''
})

export const reds = create_color_spectrum({
  description: 'A continuous sequential spectrum from light to dark red',
  display_name: 'Reds',
  colors: ['#fde8e7', red, '#600a09'],
  reference: ''
})

export const yellows = create_color_spectrum({
  description: 'A continuous sequential spectrum from light to dark yellow',
  display_name: 'Yellows',
  colors: ['#fbf6e7', yellow, '#3c2f0a'],
  reference: ''
})

// -- Sequential Multi Hue

export const plasma = create_color_spectrum({
  description: 'Perceptually-uniform color scheme designed by van der Walt, Smith and Firing for matplotlib',
  display_name: 'Plasma',
  interpolator: d3.interpolatePlasma,
  reference: 'https://bids.github.io/colormap/'
})

export const viridis = create_color_spectrum({
  description: 'Perceptually-uniform color scheme designed by van der Walt, Smith and Firing for matplotlib',
  display_name: 'Viridis',
  interpolator: d3.interpolateViridis,
  reference: 'https://bids.github.io/colormap/'
})

export const tetons_seq = create_color_spectrum({
  display_name: 'Tetons',
  colors: tetons.colors,
  reference: 'https://observablehq.com/@d3/grouped-bar-chart'
})

export const sequential = [
  blues,
  greens,
  greyscale,
  oranges,
  plasma,
  reds,
  tetons_seq,
  viridis,
  yellows
]

export const continuous = [
  ...cyclical,
  ...diverging,
  ...sequential
]
